/* eslint-disable consistent-return */
/* eslint-disable camelcase */
/* eslint-disable no-restricted-globals */
import React, { useMemo } from 'react';

import useForm from '../utils/useForm';

function MortageesList({ values }) {
  if (values?.every?.((val) => !val.mortgagee_type)) {
    return null;
  }

  return (
    <ol className="flex flex-col">
      {values.map((mortgagee, index) => (
        <li
          key={mortgagee.mortgagee_type}
          className="inline-flex min-w-[20rem] px-4 py-2 rounded-sm text-sm bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-100"
        >
          {index + 1}. {mortgagee.mortgagee_type}
        </li>
      ))}
    </ol>
  );
}

const penaltySplitOptions = [
  { value: 0, label: '0%' },
  { value: 0.1, label: '10%' },
  { value: 0.2, label: '20%' },
  { value: 0.3, label: '30%' },
  { value: 0.4, label: '40%' },
  { value: 0.5, label: '50%' },
  { value: 0.6, label: '60%' },
  { value: 0.7, label: '70%' },
  { value: 0.8, label: '80%' },
  { value: 0.9, label: '90%' },
  { value: 1, label: '100%' },
];

const cushionOptions = [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
];

const useSettingsForm = (onSubmit, values, readOnly) => {
  const fields = useMemo(() => {
    const defaultFields = {
      title: 'Settings',
      fields: [
        {
          title: 'Tax Impound Shortage',
          description:
            'How should Pixel Servicing handle tax impound shortage at the time taxes are due?',
          name: 'pref_tax_impound_shortage',
          type: 'radio',
          options: [
            {
              label:
                'Notify lender in advance. Lender will decide whether or not to advance funds at that time.',
              value: 'lender_notify',
            },
            {
              label:
                'Withdraw automatically from lender’s bank account to advance and cover shortage.',
              value: 'lender_advance',
            },
            {
              label: 'Do nothing. Taxes will NOT be paid.',
              value: 'do_nothing',
            },
          ],
        },
        {
          title: 'Insurance Cancellation',
          description:
            'How should Pixel Servicing handle insufficent insurance coverage?',
          name: 'pref_insurance_cancel',
          type: 'radio',
          options: [
            {
              label:
                'Force place automatically. Payment for forced policy will be paid first out of insurance impound (if any). Servicer will advance funds to pay remainder of forced policy balance. Servicer will demand payment from borrower, but lender is liable for borrower non-payment.',
              value: 'force_place',
            },
            {
              label: 'Do nothing. Insurance will NOT be paid.',
              value: 'do_nothing',
            },
          ],
        },
        {
          title: 'HOA Impound Shortage',
          description:
            'How should Pixel Servicing handle HOA impound shortage at the time HOA fees are due?',
          name: 'pref_hoa_impound_shortage',
          type: 'radio',
          options: [
            {
              label:
                'Notify lender in advance. Lender will decide whether or not to advance funds at that time.',
              value: 'lender_notify',
            },
            {
              label:
                'Withdraw automatically from lender’s bank account to advance and cover shortage.',
              value: 'lender_advance',
            },
            {
              label: 'Do nothing. HOA fees will NOT be paid.',
              value: 'do_nothing',
            },
          ],
        },
        {
          title: "Show Lender's Loan Number on Reports",
          name: 'pref_use_lender_loan_numbers',
          type: 'select',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
        },
        {
          title: 'Default Impound Cushion',
          description:
            'What should the default impound cushion be for new loans?',
          name: 'default_impound_cushion',
          type: 'select',
          options: cushionOptions,
        },
        {
          title: 'Default Penalty Split',
          description:
            'What should the default penalty split be for new loans?',
          name: 'default_penalty_split',
          type: 'select',
          options: penaltySplitOptions,
          readOnly,
        },
        {
          title: 'Default Payment Waterfall',
          name: 'default_payment_waterfall',
          type: 'paymentWaterfall',
          readOnly,
        },
        {
          title: 'Starting Invoice Number',
          name: 'start_invoice_number',
          type: 'number',
          readOnly,
        },
      ],
    };

    const insuranceMortgagees = {
      title: 'Default Insurance Mortgagees',
      isTitleHidden: false,
      type: 'array',
      name: 'default_insurance_mortgagees',
      isDraggable: true,
      isAddDisabled: (vals) => vals.length === 2,
      readOnly,
      fields: [
        {
          title: (vals, index, arrayIndex) => {
            return arrayIndex === 0 ? '1st Mortgagee' : '2nd Mortgagee';
          },
          name: 'mortgagee_type',
          type: 'select',
          required: true,
          customValidation: [
            'unique-type',
            'Must be unique',
            function (value) {
              const { options } = this;
              const { default_insurance_mortgagees } = options.context;

              const occurrences = default_insurance_mortgagees.filter(
                (mortgagee) => mortgagee.mortgagee_type === value
              ).length;

              // The value is unique if it appears only once
              return occurrences === 1;
            },
          ],
          options: [
            {
              label: 'Servicer',
              value: 'Servicer',
            },
            {
              label: 'Lender',
              value: 'Lender',
            },
            {
              label: 'Lien Holder',
              value: 'Lienholder',
            },
          ],
        },
      ],
    };

    const insuranceMortgageesReadOnly = {
      title: 'Default Insurance Mortgagees',
      readOnly,
      fields: [
        {
          type: 'component',
          name: 'default_insurance_mortgagees',
          component: MortageesList,
          props: {
            values: values.default_insurance_mortgagees,
          },
        },
      ],
    };

    return [
      defaultFields,
      readOnly ? insuranceMortgageesReadOnly : insuranceMortgagees,
    ];
  }, [readOnly, values.default_insurance_mortgagees]);

  return useForm({ fields, onSubmit, values });
};

export default useSettingsForm;
